import { DriveStep } from 'driver.js';
import {regionWord} from "@shared/utils/region-word.util";

export type GuidedTourStepsDeps = {
  elementExists: (selector: string) => boolean;

  // roles
  isAdmin: () => boolean;
  isSupervisor: () => boolean;
  isParticular?: () => boolean;

  // ui bridge
  openProfileMenu: () => void;
  closeProfileMenu: () => void;

  // sidebar
  ensureSidebarExpanded: () => void;

  // async helpers
  waitForElement: (selector: string, timeoutMs?: number) => Promise<void>;

  waitForNotExists?: (selector: string, timeoutMs?: number) => Promise<void>;

  moveNextSafe: () => void;

  // navegacion
  navigateAndContinue: (path: string, waitSelector: string) => void;

  // cerrar tour + navegar
  finishAndNavigate?: (path: string, waitSelector?: string) => void;
};

export function buildGeneralHeaderSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // PERFIL
  if (d.elementExists('[data-tour="header-profile"]')) {
    steps.push({
      element: '[data-tour="header-profile"]',
      popover: {
        title: 'CUENTA',
        description: 'Aqui estan tus accesos de cuenta: PERFIL, SALIR y opciones segun tu rol.',
        side: 'bottom',
        align: 'end',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'ABRIR',
        onNextClick: () => {
          d.openProfileMenu();
          d.waitForElement('[data-tour="menu-profile"]', 2500).then(() => d.moveNextSafe());
        },
      },
    } as any);

    steps.push({
      element: '[data-tour="menu-profile"]',
      popover: {
        title: 'PERFIL',
        description: 'Accede a tus datos de usuario y opciones de cuenta.',
        side: 'right',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.openProfileMenu(),
    });

    steps.push({
      element: '[data-tour="menu-logout"]',
      popover: {
        title: 'SALIR',
        description: 'Cierra tu sesion de forma segura.',
        side: 'right',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.openProfileMenu(),
    });

    if (d.isAdmin()) {
      steps.push({
        element: '[data-tour="menu-groups"]',
        popover: {
          title: 'GRUPOS',
          description: 'Aqui podras cambiar el grupo empresarial al que quiere ver la informacion.',
          side: 'right',
          align: 'start',
          prevBtnText: 'ANTERIOR',
          nextBtnText: 'SIGUIENTE',
        },
        onHighlightStarted: () => d.openProfileMenu(),
      });
    }

    if (d.isAdmin() || d.isSupervisor()) {
      steps.push({
        element: '[data-tour="menu-branches"]',
        popover: {
          title: 'SUCURSALES',
          description: 'Aqui podras cambiar de sucursal para poder filtrar los datos.',
          side: 'right',
          align: 'start',
          prevBtnText: 'ANTERIOR',
          nextBtnText: 'CONTINUAR',
          onNextClick: () => {
            d.closeProfileMenu();
            setTimeout(() => d.moveNextSafe(), 80);
          },
        },
        onHighlightStarted: () => d.openProfileMenu(),
      } as any);
    } else {
      steps.push({
        element: '[data-tour="menu-profile"]',
        popover: {
          title: 'LISTO',
          description: 'Ahora veremos el menu del lado izquierdo.',
          side: 'right',
          align: 'start',
          prevBtnText: 'ANTERIOR',
          nextBtnText: 'CONTINUAR',
          onNextClick: () => {
            d.closeProfileMenu();
            setTimeout(() => d.moveNextSafe(), 80);
          },
        },
        onHighlightStarted: () => d.openProfileMenu(),
      } as any);
    }
  }

  steps.push(...buildSidebarSteps(d));
  steps.push(...buildTableExampleSteps(d));

  return steps;
}

export function buildSidebarSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  const sidebarSteps: Array<{ selector: string; title: string; desc: string }> = [
    { selector: '[data-tour="sidebar-dashboard"]', title: 'MIS RECETAS', desc: 'Listado y gestion de tus recetas.' },
    { selector: '[data-tour="sidebar-patient-patient-list"]', title: 'PACIENTES', desc: 'Administracion de pacientes: alta, consulta y seguimiento.' },
    { selector: '[data-tour="sidebar-prescription-prescription-list"]', title: 'RECETAS', desc: 'Listado y gestion de recetas (segun permisos).' },
    { selector: '[data-tour="sidebar-prescription-prescription-supply"]', title: 'SURTIR RECETA', desc: 'Modulo para surtir recetas y registrar el suministro.' },
    { selector: '[data-tour="sidebar-admin-user-list"]', title: 'PERSONAL', desc: 'Gestion de usuarios del sistema: crear, editar y permisos segun rol.' },
    { selector: '[data-tour="sidebar-doctor-doctor-list"]', title: 'MEDICOS', desc: 'Gestion de medicos: alta, edicion y asignaciones.' },
    { selector: '[data-tour="sidebar-branch-branch-list"]', title: 'SUCURSALES', desc: 'Gestion de sucursales del grupo empresarial.' },
    { selector: '[data-tour="sidebar-catalogs-catalogs-list"]', title: 'CATALOGOS', desc: 'Configuracion base (medicamentos, diagnosticos, territoriales, etc.).' },
    { selector: '[data-tour="sidebar-catalogs-catalogs-list-v2"]', title: 'ACCESOS', desc: 'Administracion de accesos: tipos de usuario, grupos empresariales y eventos.' },
    { selector: '[data-tour="sidebar-patient-health-event-list"]', title: 'EVENTOS', desc: 'Eventos de salud que has registrado.' },
  ];

  const firstVisible = sidebarSteps.find(s => d.elementExists(s.selector));
  if (firstVisible) {
    steps.push({
      element: '[data-tour="sidebar-container"]',
      popover: {
        title: 'MENU IZQUIERDO',
        description: 'Este menu contiene los modulos disponibles para tu rol. Vamos a recorrerlos uno por uno.',
        side: 'right',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.ensureSidebarExpanded(),
    });
  }

  for (const s of sidebarSteps) {
    if (!d.elementExists(s.selector)) continue;
    steps.push({
      element: s.selector,
      popover: {
        title: s.title,
        description: s.desc,
        side: 'right',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.ensureSidebarExpanded(),
    });
  }

  return steps;
}

export function buildTableExampleSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  const route = getExampleTableRoute(d);
  if (!route) return steps;

  const navSelector = getExampleTableNavSelector(route);
  if (!navSelector || !d.elementExists(navSelector)) return steps;

  steps.push({
    element: navSelector,
    popover: {
      title: 'VAMOS A VER UNA TABLA',
      description: 'Entraremos a un modulo para explicarte los botones mas comunes (BUSCAR, CREAR, RECARGAR, EXCEL y ACCIONES).',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        d.navigateAndContinue(route.replace(/^\//, ''), '.materialTableHeader');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  steps.push({
    element: '.materialTableHeader .tbl-search-box input.search-field',
    popover: {
      title: 'BUSCAR',
      description: 'Filtra la tabla rapidamente por texto (nombre, correo, etc.).',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '.mat-row .mat-column-nombre, .mat-row .mat-column-nombre .prop_one, .mat-row .mat-column-nombre mat-cell',
    popover: {
      title: 'ABRIR DETALLE',
      description: 'Tip: si das clic en el NOMBRE del registro, entras directo al detalle / perfil.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '.materialTableHeader .tbl-export-btn li.tbl-header-btn:first-child button[mat-mini-fab]',
    popover: {
      title: 'CREAR',
      description: 'Crea un nuevo registro (segun el modulo).',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '.materialTableHeader .tbl-export-btn li.tbl-header-btn:nth-child(2) button[mat-mini-fab]',
    popover: {
      title: 'RECARGAR',
      description: 'Actualiza la informacion de la tabla sin recargar toda la pagina.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  if (d.elementExists('.materialTableHeader .export-button img')) {
    steps.push({
      element: '.materialTableHeader .export-button img',
      popover: {
        title: 'EXPORTAR A EXCEL',
        description: 'Descarga el listado para auditoria o reporteo.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  steps.push({
    element: '.mat-column-acciones',
    popover: {
      title: 'ACCIONES',
      description: 'Aqui se encuentran las acciones del registro (editar y, si eres ADMIN, tambien eliminar).',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
    },
  } as any);

  return steps;
}

function getExampleTableRoute(d: GuidedTourStepsDeps): string | null {
  if (d.elementExists('[data-tour="sidebar-admin-user-list"]')) return '/admin/user-list';
  if (d.elementExists('[data-tour="sidebar-prescription-prescription-list"]')) return '/prescription/prescription-list';
  if (d.elementExists('[data-tour="sidebar-patient-health-event-list"]')) return '/patient/health-event-list';
  return null;
}

function getExampleTableNavSelector(route: string): string | null {
  const map: Record<string, string> = {
    '/admin/user-list': '[data-tour="sidebar-admin-user-list"]',
    '/prescription/prescription-list': '[data-tour="sidebar-prescription-prescription-list"]',
    '/patient/health-event-list': '[data-tour="sidebar-patient-health-event-list"]',
  };
  return map[route] ?? null;
}

// =========================================
// ADMIN · FILTRO AVANZADO (steps)
// =========================================
export function buildAdminAdvancedFilterSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];
  const isParticular = !!d.isParticular?.();

  if (d.elementExists('[data-tour="header-profile"]')) {
    steps.push({
      element: '[data-tour="header-profile"]',
      popover: {
        title: 'FILTRO AVANZADO',
        description: isParticular
          ? 'EL FILTRO GLOBAL SE CONTROLA DESDE EL MENU DE CUENTA: SUCURSAL.'
          : 'EL FILTRO GLOBAL SE CONTROLA DESDE EL MENU DE CUENTA: GRUPO EMPRESARIAL Y SUCURSAL.',
        side: 'bottom',
        align: 'end',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'ABRIR',
        onNextClick: () => {
          d.openProfileMenu();

          const waitSelector = isParticular
            ? '[data-tour="menu-branches"]'
            : '[data-tour="menu-groups"]';

          d.waitForElement(waitSelector, 2500).then(() => d.moveNextSafe());
        },
      },
    } as any);
  }

  if (!isParticular) {
    steps.push({
      element: '[data-tour="menu-groups"]',
      popover: {
        title: 'GRUPO EMPRESARIAL',
        description:
          'AQUI ELIGES EL GRUPO EMPRESARIAL. AL CAMBIARLO, TODAS LAS TABLAS SE FILTRAN POR ESE GRUPO.',
        side: 'right',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.openProfileMenu(),
    } as any);
  }

  steps.push({
    element: '[data-tour="menu-branches"]',
    popover: {
      title: 'SUCURSAL',
      description: isParticular
        ? 'AQUI PUEDES CAMBIAR LA SUCURSAL O CONSULTORIO PARA FILTRAR LA INFORMACION QUE VAS A CONSULTAR.'
        : 'DESPUES DE ELEGIR EL GRUPO, APARECEN SUS SUCURSALES. AL ELEGIR UNA, TODAS LAS TABLAS SE FILTRAN POR ESA SUCURSAL.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
      onNextClick: () => {
        d.closeProfileMenu();
        setTimeout(() => d.moveNextSafe(), 80);
      },
    },
    onHighlightStarted: () => d.openProfileMenu(),
  } as any);

  return steps;
}

// =========================================
// ADMIN · VER RECETAS (steps)
// =========================================
export function buildAdminViewRecipesSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // =========================================
  // 0) ENTRAR A RECETAS (MENU IZQUIERDO)
  // =========================================
  if (d.elementExists('[data-tour="sidebar-prescription-prescription-list"]')) {
    steps.push({
      element: '[data-tour="sidebar-prescription-prescription-list"]',
      popover: {
        title: 'RECETAS',
        description: 'Vamos a entrar al modulo de RECETAS.',
        side: 'right',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
        onNextClick: () => {
          // Navega y espera a que aparezca el panel de filtros
          d.navigateAndContinue('prescription/prescription-list', '[data-tour="rx-filter-periodo"]');
        },
      },
      onHighlightStarted: () => d.ensureSidebarExpanded(),
    } as any);
  }

  // =========================
  // FILTROS DE ARRIBA (solo si existen)
  // =========================
  if (d.elementExists('[data-tour="rx-filter-gemp"]')) {
    steps.push({
      element: '[data-tour="rx-filter-gemp"]',
      popover: {
        title: 'GRUPO EMPRESARIAL',
        description: 'Selecciona el GRUPO EMPRESARIAL para consultar recetas.',
        side: 'bottom',
        align: 'start',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-filter-sucursal"]')) {
    steps.push({
      element: '[data-tour="rx-filter-sucursal"]',
      popover: {
        title: 'SUCURSAL',
        description: 'Opcional: filtra por SUCURSAL para acotar resultados.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-filter-fecha-inicial"]')) {
    steps.push({
      element: '[data-tour="rx-filter-fecha-inicial"]',
      popover: {
        title: 'FECHA INICIAL',
        description: 'Selecciona la fecha inicial del rango.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-filter-fecha-final"]')) {
    steps.push({
      element: '[data-tour="rx-filter-fecha-final"]',
      popover: {
        title: 'FECHA FINAL',
        description: 'Selecciona la fecha final del rango.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-filter-periodo"]')) {
    steps.push({
      element: '[data-tour="rx-filter-periodo"]',
      popover: {
        title: 'PERIODO',
        description: 'Elige si buscaras por DIA, MES o AÑO.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-filter-folio"]')) {
    steps.push({
      element: '[data-tour="rx-filter-folio"]',
      popover: {
        title: 'FOLIO',
        description: 'Opcional: busca una receta por folio.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-filter-btn-buscar"]')) {
    steps.push({
      element: '[data-tour="rx-filter-btn-buscar"]',
      popover: {
        title: 'BUSCAR',
        description: 'Presiona BUSCAR para cargar los resultados.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  // =========================
  // CONTROLES DE LA TABLA (barra azul)
  // =========================
  if (d.elementExists('[data-tour="rx-table-search"]')) {
    steps.push({
      element: '[data-tour="rx-table-search"]',
      popover: {
        title: 'BUSCAR EN LA TABLA',
        description: 'Filtra rapidamente los resultados por texto.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-btn-add"]')) {
    steps.push({
      element: '[data-tour="rx-btn-add"]',
      popover: {
        title: 'CREAR',
        description: 'Crea una nueva receta (si tu rol lo permite).',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-btn-refresh"]')) {
    steps.push({
      element: '[data-tour="rx-btn-refresh"]',
      popover: {
        title: 'RECARGAR',
        description: 'Actualiza la tabla con los datos mas recientes.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-btn-excel"]')) {
    steps.push({
      element: '[data-tour="rx-btn-excel"]',
      popover: {
        title: 'EXPORTAR A EXCEL',
        description: 'Descarga el listado de recetas en Excel.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  // (Opcional) si tienes un boton/icono de filtro en la barra
  if (d.elementExists('[data-tour="rx-btn-filter"]')) {
    steps.push({
      element: '[data-tour="rx-btn-filter"]',
      popover: {
        title: 'FILTROS',
        description: 'Abre filtros adicionales del listado (si estan disponibles).',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  // =========================
  // ACCIONES (columna y botones)
  // =========================

  // Contenedor general (existira solo si hay filas)
  if (d.elementExists('[data-tour="rx-actions-cell"]')) {
    steps.push({
      element: '[data-tour="rx-actions-cell"]',
      popover: {
        title: 'ACCIONES',
        description: 'En cada receta veras acciones disponibles segun tu rol y el estatus de la receta.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  } else {
    // Si no hay filas, cerramos con un mensaje util
    if (d.elementExists('[data-tour="rx-table"]')) {
      steps.push({
        element: '[data-tour="rx-table"]',
        popover: {
          title: 'SIN RESULTADOS',
          description: 'Realiza una busqueda para que aparezcan recetas y puedas ver las ACCIONES disponibles.',
          side: 'top',
          align: 'start',
          prevBtnText: 'ANTERIOR',
          doneBtnText: 'TERMINAR',
        },
      } as any);
    }
    return steps;
  }

  // Cada boton se agrega solo si existe (si no existe, se omite)
  if (d.elementExists('[data-tour="rx-action-view"]')) {
    steps.push({
      element: '[data-tour="rx-action-view"]',
      popover: {
        title: 'VER DETALLE',
        description: 'Abre el detalle completo de la receta.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-action-edit"]')) {
    steps.push({
      element: '[data-tour="rx-action-edit"]',
      popover: {
        title: 'EDITAR',
        description: 'Disponible cuando la receta NO esta timbrada y NO esta cancelada (solo Doctor).',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-action-pdf"]')) {
    steps.push({
      element: '[data-tour="rx-action-pdf"]',
      popover: {
        title: 'DESCARGAR PDF',
        description: 'Disponible cuando la receta esta timbrada y se permite generar PDF.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-action-timbrar"]')) {
    steps.push({
      element: '[data-tour="rx-action-timbrar"]',
      popover: {
        title: 'TIMBRAR',
        description: 'Envia la receta a timbrado (si no esta timbrada y no esta cancelada, solo Doctor).',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  if (d.elementExists('[data-tour="rx-action-delete"]')) {
    steps.push({
      element: '[data-tour="rx-action-delete"]',
      popover: {
        title: 'ELIMINAR',
        description: 'Elimina la receta cuando aun no tiene folio y no esta cancelada (solo Doctor).',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
    } as any);
  }

  // =========================
  // CIERRE: TABLA
  // =========================
  if (d.elementExists('[data-tour="rx-table"]')) {
    steps.push({
      element: '[data-tour="rx-table"]',
      popover: {
        title: 'LISTO',
        description: 'Con estos filtros, botones y acciones ya puedes gestionar tus recetas.',
        side: 'top',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        doneBtnText: 'TERMINAR',
      },
    } as any);
  }

  return steps;
}

// =========================================
// ADMIN · ALTA DE CLIENTE (PRIMEROS PASOS)
// Reutiliza sub-tours: crear grupo, crear sucursal, crear personal
// =========================================
export function buildAdminOnboardingClientSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // 0) Intro rapido
  if (d.elementExists('[data-tour="sidebar-catalogs-catalogs-list-v2"]')) {
    steps.push({
      element: '[data-tour="sidebar-catalogs-catalogs-list-v2"]',
      popover: {
        title: 'ALTA DE CLIENTE',
        description:
          'Este tutorial es para cuando llega un CLIENTE NUEVO: ' +
          'crearas su GRUPO EMPRESARIAL, seleccionaras ese grupo en tu cuenta, ' +
          'crearas SUCURSAL y al final crearas PERSONAL con sus CREDENCIALES.',
        side: 'right',
        align: 'start',
        nextBtnText: 'EMPEZAR',
        prevBtnText: 'ANTERIOR',
        onNextClick: () => {
          d.navigateAndContinue('catalogs/catalogs-list-v2', '[data-tour="accesos-tab-groups"]');
        },
      },
      onHighlightStarted: () => d.ensureSidebarExpanded(),
    } as any);
  }

  // 1) Crear Grupo Empresarial (sub-tour)
  steps.push(...buildAdminCrearGrupoEmpresarialSteps(d));

  // 2) Seleccionar Grupo desde el menu de cuenta
  // (No forzamos click a un item especifico porque depende del nombre/orden del grupo)
  if (d.elementExists('[data-tour="header-profile"]')) {
    steps.push({
      element: '[data-tour="header-profile"]',
      popover: {
        title: 'SELECCIONAR GRUPO',
        description:
          'Ahora abre tu menu de CUENTA y entra a GRUPOS para seleccionar el GRUPO que acabas de crear. ' +
          'Esto es importante porque SUCURSALES y PERSONAL se crean DENTRO del grupo seleccionado.',
        side: 'bottom',
        align: 'end',
        nextBtnText: 'ABRIR',
        prevBtnText: 'ANTERIOR',
        onNextClick: () => {
          d.openProfileMenu();
          d.waitForElement('[data-tour="menu-groups"]', 2500).then(() => d.moveNextSafe());
        },
      },
    } as any);

    steps.push({
      element: '[data-tour="menu-groups"]',
      popover: {
        title: 'GRUPOS',
        description:
          'Aqui veras la lista de grupos. Selecciona el NUEVO GRUPO EMPRESARIAL que acabas de crear.',
        side: 'right',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'LISTO',
        onNextClick: () => {
          d.closeProfileMenu();
          setTimeout(() => d.moveNextSafe(), 120);
        },
      },
      onHighlightStarted: () => d.openProfileMenu(),
    } as any);
  }

  // 3) Crear Sucursal (sub-tour)
  steps.push(...buildAdminCrearSucursalSteps(d));

  // 4) Crear Personal (sub-tour) + cierre con credenciales
  steps.push(...buildAdminCrearPersonalSteps(d));

  // 5) Cierre final (mensaje de salida)
  steps.push({
    element: 'body',
    popover: {
      title: 'LISTO ✅',
      description:
        'CON ESTO YA CREASTE A TU PRIMER CLIENTE: ' +
        'GRUPO EMPRESARIAL + SUCURSAL + PERSONAL.\n\n' +
        'AHORA SOLO FALTA COMPARTIRLE AL CLIENTE LAS CREDENCIALES DEL USUARIO (USUARIO Y CONTRASEÑA) ' +
        'PARA QUE PUEDA ACCEDER A LA PLATAFORMA.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'FINALIZAR',
    },
  } as any);


  return steps;
}

// =========================================
// SUB-TOUR · CREAR GRUPO EMPRESARIAL
// =========================================
export function buildAdminCrearGrupoEmpresarialSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // 1) Forzar pestaña "GRUPOS EMPRESARIALES"
  steps.push({
    element: '[data-tour="accesos-tabs"]',
    popover: {
      title: 'GRUPOS EMPRESARIALES',
      description: 'Vamos a abrir la pestaña de GRUPOS EMPRESARIALES para crear el grupo del cliente.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR PESTAÑA',
      onNextClick: () => {
        // Click al header real del tab 1 (0=Tipos, 1=Grupos, 2=Eventos)
        const headerLabels = document.querySelectorAll(
          '[data-tour="accesos-tabs"] .mat-mdc-tab'
        ) as NodeListOf<HTMLElement>;

        headerLabels?.[1]?.click();

        // Espera a que se renderice el contenido lazy
        d.waitForElement('[data-tour="gemp-btn-add"]', 8000).then(() => d.moveNextSafe());
      },
    },
  } as any);

  // 2) Boton "+" (CREAR GRUPO) — SIEMPRE se agrega (aunque no exista todavia)
  steps.push({
    element: '[data-tour="gemp-btn-add"]',
    popover: {
      title: 'CREAR GRUPO',
      description: 'Haz clic en el boton “+” para registrar un nuevo GRUPO EMPRESARIAL.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR FORMULARIO',
      onNextClick: () => {
        const btn = document.querySelector('[data-tour="gemp-btn-add"]') as HTMLElement | null;
        btn?.click();

        d.waitForElement('[data-tour="gemp-modal"]', 8000).then(() => d.moveNextSafe());
      },
    },
    // 🔒 Importante: antes de resaltar este step, esperamos el boton
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="gemp-btn-add"]', 8000);
    },
  } as any);

  // 3) Modal: Nombre
  steps.push({
    element: '[data-tour="gemp-input-nombre"]',
    popover: {
      title: 'NOMBRE',
      description:
        'Nombre completo del grupo/cliente. Regla: solo LETRAS y ESPACIOS (sin numeros). Ejemplo: “CLINICA SAN JOSE”.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="gemp-input-nombre"]', 8000);
    },
  } as any);

  // 4) Modal: Abreviatura
  steps.push({
    element: '[data-tour="gemp-input-abreviatura"]',
    popover: {
      title: 'ABREVIATURA',
      description:
        'Clave corta del grupo. Regla: solo LETRAS y maximo 3 caracteres. Ejemplo: “CSJ”, “ABC”.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="gemp-input-abreviatura"]', 8000);
    },
  } as any);

  // 5) Modal: Logo
  steps.push({
    element: '[data-tour="gemp-input-logo"]',
    popover: {
      title: 'LOGO',
      description: 'Sube el logo del cliente para identificar el grupo rapidamente.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="gemp-input-logo"]', 8000);
    },
  } as any);

  // 6) Modal: Guardar (y luego cerrar para continuar)
  steps.push({
    element: '[data-tour="gemp-btn-guardar"]',
    popover: {
      title: 'GUARDAR',
      description:
        'Cuando llenes todo, guarda el grupo. Si no quieres crear datos ahora, puedes cerrar el modal y continuar.',
      side: 'top',
      align: 'end',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'CONTINUAR',
      onNextClick: () => {
        // No forzamos guardar. Cerramos el modal y seguimos.
        const closeBtn = document.querySelector('[data-tour="gemp-modal-close"]') as HTMLElement | null;
        closeBtn?.click();
        setTimeout(() => d.moveNextSafe(), 250);
      },
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="gemp-btn-guardar"]', 8000);
    },
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · CREAR SUCURSAL
// =========================================
export function buildAdminCrearSucursalSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // 1) Ir a SUCURSALES desde menu
  steps.push({
    element: '[data-tour="sidebar-branch-branch-list"]',
    popover: {
      title: 'SUCURSALES',
      description: 'Ahora vamos a crear la PRIMERA SUCURSAL del cliente. Entra al modulo de SUCURSALES.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        // Navega y espera a que exista el "+"
        d.navigateAndContinue('branch/branch-list', '[data-tour="branch-btn-add"]');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  // 2) Opcion 1: "+" de la tabla (SIEMPRE)
  steps.push({
    element: '[data-tour="branch-btn-add"]',
    popover: {
      title: 'OPCION 1: CREAR DESDE LA TABLA',
      description: 'Puedes crear una sucursal dando clic en el boton “+” de la tabla.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="branch-btn-add"]', 8000);
    },
  } as any);

  // 3) Opcion 2: Dashboard (SIEMPRE)
  steps.push({
    element: '[data-tour="dashboard-create-branch"]',
    popover: {
      title: 'OPCION 2: CREAR DESDE EL DASHBOARD',
      description: 'O tambien puedes crearla desde el DASHBOARD con el boton “NUEVA SUCURSAL”.',
      side: 'bottom',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'CONTINUAR',
    },
    onHighlightStarted: () => {
    },
  } as any);

  // 4) Abrir formulario (smart: si ya esta abierto, no hace click)
  steps.push({
    element: '[data-tour="branch-btn-add"]',
    popover: {
      title: 'ABRIR FORMULARIO',
      description: 'Ahora abriremos el formulario para capturar los datos de la sucursal.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR',
      onNextClick: () => {
        // ✅ Si ya estamos en el formulario, no hacemos click, solo avanzamos
        if (document.querySelector('[data-tour="branch-form"]')) {
          d.moveNextSafe();
          return;
        }

        // Si no esta abierto, entonces si abrimos
        const btnPlus = document.querySelector('[data-tour="branch-btn-add"]') as HTMLElement | null;
        const btnDash = document.querySelector('[data-tour="dashboard-create-branch"]') as HTMLElement | null;

        (btnPlus || btnDash)?.click();

        d.waitForElement('[data-tour="branch-form"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => {
      // Si ya existe, no esperes (evita sensacion de “trabado”)
      if (!document.querySelector('[data-tour="branch-form"]')) {
        d.waitForElement('[data-tour="branch-btn-add"]', 8000);
      }
    },
  } as any);


  // 5) Campos del formulario (igual que ya los traias)
  const fields: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="branch-nombre"]', title: 'NOMBRE', desc: 'Solo letras y espacios. Ej: “SUCURSAL CENTRO”.' },
    { sel: '[data-tour="branch-registro"]', title: 'REGISTRO SANITARIO', desc: 'Respeta el formato (letras/numeros/guiones).' },
    { sel: '[data-tour="branch-cp-colonia"]', title: regionWord('CODIGO POSTAL / COLONIA'), desc: regionWord('Escribe el CP y selecciona la colonia sugerida.') },
    { sel: '[data-tour="branch-domicilio"]', title: 'DIRECCION', desc: 'Calle, numero y referencias.' },
    { sel: '[data-tour="branch-responsable"]', title: 'RESPONSABLE SANITARIO', desc: 'Solo letras y espacios.' },
    { sel: '[data-tour="branch-email"]', title: 'CORREO', desc: 'Correo valido del responsable.' },
    { sel: '[data-tour="branch-cedula"]', title: 'MATRICULA', desc: '6 a 8 digitos (solo numeros).' },
    { sel: '[data-tour="branch-telefono"]', title: 'TELEFONO', desc: 'Telefono valido (puede llevar +52).' },
  ];

  for (const f of fields) {
    steps.push({
      element: f.sel,
      popover: {
        title: f.title,
        description: f.desc,
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => {
        d.waitForElement(f.sel, 8000);
      },
    } as any);
  }

  steps.push({
    element: '[data-tour="branch-btn-guardar"]',
    popover: {
      title: 'GUARDAR SUCURSAL',
      description: 'Cuando el formulario este completo, presiona este boton para guardar la sucursal.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="branch-btn-guardar"]', 8000),
  } as any);


  return steps;
}

// =========================================
// SUB-TOUR · CREAR PERSONAL (y explicar credenciales)
// =========================================
export function buildAdminCrearPersonalSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // 1) Ir a PERSONAL desde menu
  steps.push({
    element: '[data-tour="sidebar-admin-user-list"]',
    popover: {
      title: 'PERSONAL',
      description: 'Ahora vamos a crear el PERSONAL del cliente (usuarios que van a entrar al sistema).',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        d.navigateAndContinue('admin/user-list', '[data-tour="personal-create"]');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  // 2) Opcion 1: "+" de la tabla (SIEMPRE)
  steps.push({
    element: '[data-tour="personal-create"]',
    popover: {
      title: 'OPCION 1: CREAR DESDE LA TABLA',
      description: 'Puedes crear un usuario dando clic en el boton “+” de la tabla.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="personal-create"]', 8000);
    },
  } as any);

  // 3) Opcion 2: Dashboard (SIEMPRE)
  steps.push({
    element: '[data-tour="dashboard-create-personal"]',
    popover: {
      title: 'OPCION 2: CREAR DESDE EL DASHBOARD',
      description: 'O tambien puedes crear un usuario desde el DASHBOARD con el boton “NUEVO PERSONAL”.',
      side: 'bottom',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'CONTINUAR',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="dashboard-create-personal"]', 8000);
    },
  } as any);

  // 4) Abrir formulario (prioridad: +, si no, dashboard)
  steps.push({
    element: '[data-tour="personal-create"]',
    popover: {
      title: 'ABRIR FORMULARIO',
      description: 'Ahora abriremos el formulario para registrar el usuario.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR',
      onNextClick: () => {
        const btnPlus = document.querySelector('[data-tour="personal-create"]') as HTMLElement | null;
        const btnDash = document.querySelector('[data-tour="dashboard-create-personal"]') as HTMLElement | null;

        (btnPlus || btnDash)?.click();

        d.waitForElement('[data-tour="user-form"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="personal-create"]', 8000);
    },
  } as any);

  // 5) Campos (los tuyos reales)
  const fieldsBase: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="user-tipo"]', title: 'TIPO DE USUARIO', desc: 'Define permisos. Elige el rol correcto antes de llenar lo demas.' },
    { sel: '[data-tour="user-usr"]', title: 'USUARIO', desc: 'Sera el LOGIN. Ej: “recepcion_01” / “juan.perez”. Evita espacios.' },
    { sel: '[data-tour="user-password"]', title: 'CONTRASEÑA', desc: 'Mínimo 8 caracteres. Debe incluir 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial (! @ # $ % ^ & * ( ) , . ? " : { } | < >).' },
    { sel: '[data-tour="user-email"]', title: 'CORREO', desc: 'Correo valido. Se usa para notificaciones y recuperacion.' },

    { sel: '[data-tour="user-nombres"]', title: 'NOMBRE(S)', desc: 'Solo letras y espacios. Ej: “JUAN CARLOS”.' },
    { sel: '[data-tour="user-apellido-paterno"]', title: 'PRIMER APELLIDO', desc: 'Solo letras y espacios.' },
    { sel: '[data-tour="user-apellido-materno"]', title: 'SEGUNDO APELLIDO', desc: 'Solo letras y espacios.' },

    { sel: '[data-tour="user-movil"]', title: 'TELEFONO', desc: 'Elige el código de país (ej. +52) y escribe el número móvil de 10 dígitos.' },
    { sel: '[data-tour="user-cp-colonia"]', title: regionWord('CODIGO POSTAL / COLONIA'), desc: regionWord('Escribe el CP y selecciona una colonia sugerida.') },
    { sel: '[data-tour="user-domicilio"]', title: 'DIRECCION', desc: 'Calle, numero y referencias. Ej: “AV. REFORMA 123, INT 2”.' },
  ];

  const fieldsAdminOnly: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="user-gemp"]', title: 'GRUPO EMPRESARIAL', desc: 'Solo ADMIN: el grupo donde pertenecera el usuario.' },
  ];

  const fieldsAdminOrSupervisor: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="user-sucursal"]', title: 'SUCURSAL', desc: 'Asigna la sucursal donde trabajara el usuario.' },
  ];

  const fieldsExtras: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="user-imagen"]', title: 'IMAGEN', desc: 'Opcional, pero ayuda a identificar al usuario.' },
  ];

  const fieldsFinal = [
    ...fieldsBase,
    ...(d.isAdmin() ? fieldsAdminOnly : []),
    ...((d.isAdmin() || d.isSupervisor()) ? fieldsAdminOrSupervisor : []),
    ...fieldsExtras,
  ];

  for (const f of fieldsFinal) {
    steps.push({
      element: f.sel,
      popover: {
        title: f.title,
        description: f.desc,
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.waitForElement(f.sel, 8000),
    } as any);
  }

  // 6) Cierre
  steps.push({
    element: '[data-tour="user-usr"]',
    popover: {
      title: 'CREDENCIALES',
      description:
        'IMPORTANTE: el “USUARIO” y la “CONTRASEÑA” que captures aqui son las credenciales que le entregaras al personal del cliente.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'VER BOTON FINAL',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="user-usr"]', 8000);
    },
  } as any);

  steps.push({
    element: '[data-tour="user-btn-guardar"]',
    popover: {
      title: 'CREAR USUARIO',
      description: 'Cuando el formulario este completo, presiona este boton para crear el usuario.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="user-btn-guardar"]', 8000);
    },
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · CREAR MEDICO (y explicar credenciales)
// =========================================
export function buildAdminCrearMedicoSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // 1) Entrar a medicos
  steps.push({
    element: '[data-tour="sidebar-doctor-doctor-list"]',
    popover: {
      title: 'MEDICOS',
      description: 'Vamos a crear un MEDICO.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        d.navigateAndContinue('doctor/doctor-list', '[data-tour="doctor-create"]');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  // 2) Opcion 1: +
  steps.push({
    element: '[data-tour="doctor-create"]',
    popover: {
      title: 'OPCION 1: CREAR DESDE LA TABLA',
      description: 'Puedes crear un medico desde el boton “+” de la tabla.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="doctor-create"]', 8000),
  } as any);

  // 3) Opcion 2: Dashboard (informativo, NO bloquea)
  steps.push({
    element: '[data-tour="dashboard-create-doctor"]',
    popover: {
      title: 'OPCION 2: CREAR DESDE EL DASHBOARD',
      description:
        'Tambien puedes crear un medico desde el DASHBOARD con el boton “NUEVO MEDICO”.',
      side: 'bottom',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'CONTINUAR',
    },
    onHighlightStarted: () => {
      // No esperar, para que no “cuelgue” si no estas en dashboard
    },
  } as any);

  // 4) Abrir formulario (prioridad: +, si no existe, dashboard)
  steps.push({
    element: '[data-tour="doctor-create"]',
    popover: {
      title: 'ABRIR FORMULARIO',
      description: 'Ahora abriremos el formulario para registrar al medico.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR',
      onNextClick: () => {
        // ✅ si ya esta abierto, avanzamos
        if (document.querySelector('[data-tour="doctor-form"]')) {
          d.moveNextSafe();
          return;
        }

        // Prioridad: boton +; fallback: dashboard
        const btnPlus = document.querySelector('[data-tour="doctor-create"]') as HTMLElement | null;
        const btnDash = document.querySelector('[data-tour="dashboard-create-doctor"]') as HTMLElement | null;
        (btnPlus || btnDash)?.click();

        d.waitForElement('[data-tour="doctor-form"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="doctor-create"]', 8000),
  } as any);

  // 5) Campos (condicionados por rol, sin brincarse por DOM timing)
  const fieldsBase: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="doctor-usr"]', title: 'USUARIO', desc: 'LOGIN del medico. Ej: “dr.garcia”. Evita espacios.' },
    { sel: '[data-tour="doctor-password"]', title: 'CONTRASEÑA', desc: 'Mínimo 8 caracteres. Debe incluir 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial (! @ # $ % ^ & * ( ) , . ? " : { } | < >).' },
    { sel: '[data-tour="doctor-email"]', title: 'CORREO', desc: 'Correo valido (recuperacion / notificaciones).' },

    { sel: '[data-tour="doctor-nombres"]', title: 'NOMBRE(S)', desc: 'Solo letras y espacios.' },
    { sel: '[data-tour="doctor-apellido-paterno"]', title: 'PRIMER APELLIDO', desc: 'Solo letras y espacios.' },
    { sel: '[data-tour="doctor-apellido-materno"]', title: 'SEGUNDO APELLIDO', desc: 'Solo letras y espacios.' },

    { sel: '[data-tour="doctor-movil"]', title: 'TELEFONO', desc: 'Elige el código de país (ej. +52) y escribe el número móvil de 10 dígitos.' },
    { sel: '[data-tour="doctor-cp-colonia"]', title: regionWord('CODIGO POSTAL / COLONIA'), desc: regionWord('Escribe el CP y selecciona una colonia sugerida.') },
    { sel: '[data-tour="doctor-domicilio"]', title: 'DIRECCION', desc: 'Calle, numero y referencias.' },
  ];

  const fieldsAdminOnly: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="doctor-gemp"]', title: 'GRUPO EMPRESARIAL', desc: 'Solo ADMIN: grupo donde quedara registrado.' },
  ];

  const fieldsAdminOrSupervisor: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="doctor-sucursal"]', title: 'SUCURSAL', desc: 'Sucursal donde atendera el medico.' },
  ];

  const fieldsProfesionales: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="doctor-cedula-general"]', title: 'CEDULA GENERAL', desc: 'Solo numeros. Verifica que sea correcta.' },
    { sel: '[data-tour="doctor-universidad"]', title: 'UNIVERSIDAD', desc: 'Nombre de la universidad (texto).' },
    { sel: '[data-tour="doctor-especialidad"]', title: 'ESPECIALIDAD', desc: 'Opcional: si aplica, selecciona la especialidad.' },
    { sel: '[data-tour="doctor-cedula-especialidad"]', title: 'CEDULA ESPECIALIDAD', desc: 'Opcional: si tiene especialidad.' },
    { sel: '[data-tour="doctor-horario"]', title: 'HORARIO', desc: 'Define el horario de atencion del medico.' },

    { sel: '[data-tour="doctor-imagen"]', title: 'IMAGEN', desc: 'Opcional. Foto de perfil.' },
    { sel: '[data-tour="doctor-firma"]', title: 'FIRMA', desc: 'Opcional. Se usa para documentos si aplica.' },
  ];

  const fieldsFinal = [
    ...fieldsBase,
    ...(d.isAdmin() ? fieldsAdminOnly : []),
    ...((d.isAdmin() || d.isSupervisor()) ? fieldsAdminOrSupervisor : []),
    ...fieldsProfesionales,
  ];

  for (const f of fieldsFinal) {
    steps.push({
      element: f.sel,
      popover: {
        title: f.title,
        description: f.desc,
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      // ✅ Aqui esta la clave: cuando llegue el paso, espera el input
      onHighlightStarted: () => d.waitForElement(f.sel, 8000),
    } as any);
  }


  // 6) Guardar
  steps.push({
    element: '[data-tour="doctor-btn-guardar"]',
    popover: {
      title: 'GUARDAR MEDICO',
      description: 'Cuando el formulario este completo, presiona este boton para guardar.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="doctor-btn-guardar"]', 8000),
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · CREAR PACIENTE (y explicar credenciales)
// =========================================
export function buildDoctorCrearPacienteSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  // 1) Ir a PACIENTES desde menu
  steps.push({
    element: '[data-tour="sidebar-patient-patient-list"]',
    popover: {
      title: 'PACIENTES',
      description: 'Ahora vamos a crear un NUEVO PACIENTE.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        d.navigateAndContinue('patient/patient-list', '[data-tour="patient-create"]');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  // 2) Opcion 1: "+" de la tabla
  steps.push({
    element: '[data-tour="patient-create"]',
    popover: {
      title: 'OPCION 1: CREAR DESDE LA TABLA',
      description: 'Puedes crear un paciente dando clic en el boton “+” de la tabla.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="patient-create"]', 8000);
    },
  } as any);

  // 3) Opcion 2: Dashboard (si existe)
  steps.push({
    element: '[data-tour="dashboard-create-patient"]',
    popover: {
      title: 'OPCION 2: CREAR DESDE EL DASHBOARD',
      description: 'Tambien puedes crearlo desde el DASHBOARD con “NUEVO PACIENTE”.',
      side: 'bottom',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'CONTINUAR',
    },
  } as any);

  // 4) Abrir formulario (smart: si ya esta abierto, no hace click)
  steps.push({
    element: '[data-tour="patient-create"]',
    popover: {
      title: 'ABRIR FORMULARIO',
      description: 'Ahora abriremos el formulario para capturar los datos del paciente.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR',
      onNextClick: () => {
        // ✅ Si ya estamos en el formulario, no hacemos click
        if (document.querySelector('[data-tour="patient-form"]')) {
          d.moveNextSafe();
          return;
        }

        // Si no esta abierto, entonces si abrimos
        const btnPlus = document.querySelector('[data-tour="patient-create"]') as HTMLElement | null;
        const btnDash = document.querySelector('[data-tour="dashboard-create-patient"]') as HTMLElement | null;

        (btnPlus || btnDash)?.click();

        d.waitForElement('[data-tour="patient-form"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => {
      if (!document.querySelector('[data-tour="patient-form"]')) {
        d.waitForElement('[data-tour="patient-create"]', 8000);
      }
    },
  } as any);

  // 5) Campos del formulario (SIEMPRE, igual que sucursal)
  const fields: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="patient-usr"]', title: 'USUARIO', desc: 'LOGIN del paciente. Ej: “paciente.01”. Evita espacios.' },
    { sel: '[data-tour="patient-password"]', title: 'CONTRASEÑA', desc: 'Mínimo 8 caracteres. Debe incluir 1 mayúscula, 1 minúscula, 1 número y 1 carácter especial (! @ # $ % ^ & * ( ) , . ? " : { } | < >).' },
    { sel: '[data-tour="patient-email"]', title: 'CORREO', desc: 'Correo valido (recuperacion / notificaciones).' },

    { sel: '[data-tour="patient-nombres"]', title: 'NOMBRE(S)', desc: 'Solo letras y espacios.' },
    { sel: '[data-tour="patient-apellido-paterno"]', title: 'PRIMER APELLIDO', desc: 'Solo letras y espacios.' },
    { sel: '[data-tour="patient-apellido-materno"]', title: 'SEGUNDO APELLIDO', desc: 'Solo letras y espacios.' },

    { sel: '[data-tour="patient-movil"]', title: 'TELEFONO', desc: 'Elige el código de país (ej. +52) y escribe el número móvil de 10 dígitos.' },
    { sel: '[data-tour="patient-cp-colonia"]', title: regionWord('CODIGO POSTAL / COLONIA'), desc: regionWord('Escribe el CP y selecciona una colonia sugerida.') },
    { sel: '[data-tour="patient-domicilio"]', title: 'DIRECCION', desc: 'Calle, numero y referencias.' },

    { sel: '[data-tour="patient-fecha-nacimiento"]', title: 'FECHA DE NACIMIENTO', desc: 'Se usa para calcular edad y expediente.' },
    { sel: '[data-tour="patient-genero"]', title: 'GENERO', desc: 'Selecciona una opcion.' },
    { sel: '[data-tour="patient-entidad-nacimiento"]', title: 'ENTIDAD DE NACIMIENTO', desc: 'Selecciona la entidad.' },
    { sel: '[data-tour="patient-tipo-identificacion"]', title: 'IDENTIFICACION', desc: 'Selecciona el tipo de identificacion.' },
    { sel: '[data-tour="patient-numero-identificacion"]', title: 'NUMERO IDENTIFICACION', desc: 'Captura el numero tal como aparece en el documento.' },

    {
      sel: '[data-tour="patient-patologias"]',
      title: 'PATOLOGÍAS',
      desc:
        '<strong>CAMPO OPCIONAL</strong><br>' +
        'EN ESTE CAMPO SE REGISTRARÁN LAS PATOLOGÍAS QUE PRESENTA EL PACIENTE AL MOMENTO DEL ALTA EN EL SISTEMA.<br>' +
        'UNA VEZ REGISTRADAS, SE LISTARÁN A LA IZQUIERDA DEL FORMULARIO.'
    },
    {
      sel: '[data-tour="patient-alergias"]',
      title: 'ALERGIAS',
      desc:
        '<strong>CAMPO OPCIONAL</strong><br>' +
        'EN ESTE CAMPO SE REGISTRARÁN LAS ALERGIAS QUE PRESENTA EL PACIENTE AL MOMENTO DEL ALTA EN EL SISTEMA.<br>' +
        'UNA VEZ REGISTRADAS, SE LISTARÁN A LA IZQUIERDA DEL FORMULARIO.'
    },
    {
      sel: '[data-tour="patient-moleculas"]',
      title: 'MOLÉCULAS',
      desc:
        '<strong>CAMPO OPCIONAL</strong><br>' +
        'EN ESTE CAMPO SE REGISTRAN LAS SUSTANCIAS O MEDICAMENTOS QUE PROVOCAN REACCIONES ALÉRGICAS EN EL PACIENTE.<br>' +
        'UNA VEZ REGISTRADAS, SE LISTARÁN A LA IZQUIERDA DEL FORMULARIO.'
    },
  ];

  for (const f of fields) {
    steps.push({
      element: f.sel,
      popover: {
        title: f.title,
        description: f.desc,
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => {
        d.waitForElement(f.sel, 8000);
      },
    } as any);
  }

  // 6) ✅ Panel izquierdo: donde se muestran Patologias/Alergias/Moleculas
  steps.push({
    element: '[data-tour="patient-left-summary"]', // Ajusta si tu selector real es otro
    popover: {
      title: 'ANTECEDENTES (PANEL IZQUIERDO)',
      description:
        'Todo lo que agregues en PATOLOGIAS, ALERGIAS y MOLECULAS se mostrara aqui. ' +
        'Puedes eliminar elementos con el boton de “X”.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="patient-left-summary"]', 8000);
    },
  } as any);

  // 7) Imagen (si aplica)
  steps.push({
    element: '[data-tour="patient-imagen"]',
    popover: {
      title: 'IMAGEN',
      description: 'Foto (opcional) para el perfil del paciente.',
      side: 'top',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="patient-imagen"]', 8000),
  } as any);

  // 8) Guardar
  steps.push({
    element: '[data-tour="patient-btn-guardar"]',
    popover: {
      title: 'GUARDAR PACIENTE',
      description: 'Cuando el formulario este completo, presiona este boton para guardar.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="patient-btn-guardar"]', 8000),
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · CREAR RECETA (MEDICO / PARTICULAR)
// =========================================
export function buildDoctorCrearRecetaSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  steps.push({
    element: '[data-tour="rx-create-prescription-entry"], [data-tour="top-widget-create-prescription"]',
    popover: {
      title: 'CREAR RECETA',
      description:
        'Desde aqui puedes iniciar una nueva receta. El acceso puede estar en el boton de agregar de la lista o en el widget superior.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        d.navigateAndContinue('prescription/tutorial-create', '[data-tour="rx-panel-paciente"]');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  steps.push({
    element: '[data-tour="rx-panel-paciente"]',
    popover: {
      title: 'DATOS DEL PACIENTE',
      description:
        'Primero se selecciona al paciente. Despues de elegirlo, el sistema muestra su resumen clinico para apoyar la prescripcion.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="rx-panel-paciente"]', 8000);
      const cmp = (window as any)?.ng?.getComponent?.(
        document.querySelector('app-prescription-edit-demo')
      );
      cmp?.showPatientFlow?.();
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-patient-search"]',
    popover: {
      title: 'BUSCAR PACIENTE',
      description:
        'Aqui escribes el nombre del paciente para localizarlo dentro del sistema y seleccionarlo antes de crear la receta.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="rx-patient-search"]', 8000);
      const cmp = (window as any)?.ng?.getComponent?.(
        document.querySelector('app-prescription-edit-demo')
      );
      cmp?.showPatientFlow?.();
      cmp?.openPatientAutocomplete?.();
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-patient-results"]',
    popover: {
      title: 'RESULTADOS DE PACIENTES',
      description:
        'Aqui aparecen las coincidencias de la busqueda. Selecciona el paciente correcto para continuar con la receta.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SELECCIONAR',
      onNextClick: () => {
        const cmp = (window as any)?.ng?.getComponent?.(
          document.querySelector('app-prescription-edit-demo')
        );
        cmp?.selectPatient?.();

        d.waitForElement('[data-tour="rx-panel-consulta"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="rx-patient-results"]', 8000);
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-panel-paciente"] .rx-summary-card',
    popover: {
      title: 'RESUMEN CLINICO DEL PACIENTE',
      description:
        'Despues de seleccionar al paciente, se muestra un resumen clinico. Aqui puedes revisar antecedentes y medicamentos activos registrados antes de prescribir.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () =>
      d.waitForElement('[data-tour="rx-panel-paciente"] .rx-summary-card', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-panel-consulta"]',
    popover: {
      title: 'DATOS DE CONSULTA',
      description:
        'En esta seccion se capturan los datos clinicos de la consulta. Peso y altura ayudan a contextualizar mejor la prescripcion.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-panel-consulta"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-weight"]',
    popover: {
      title: 'PESO DEL PACIENTE',
      description: 'Captura el peso del paciente en kilogramos.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-weight"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-height"]',
    popover: {
      title: 'ALTURA DEL PACIENTE',
      description:
        'Captura la altura del paciente en centimetros. Si aplica embarazo o lactancia, tambien pueden mostrarse las semanas.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-height"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-panel-diagnostico"]',
    popover: {
      title: 'DIAGNOSTICO',
      description:
        'Aqui agregas patologias y alergias relacionadas con la consulta actual.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-panel-diagnostico"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-search-patologias"]',
    popover: {
      title: 'BUSCAR PATOLOGIA',
      description:
        'Aqui puedes buscar una patologia para agregarla al diagnostico de la consulta.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-search-patologias"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-cronico-checkbox"]',
    popover: {
      title: 'CRONICO',
      description:
        'Este checkbox indica si la patologia debe considerarse cronica, es decir, una condicion persistente del paciente y no solo algo aislado de esta consulta.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-cronico-checkbox"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-search-alergias"]',
    popover: {
      title: 'BUSCAR ALERGIA',
      description:
        'Aqui puedes buscar alergias o moleculas para agregarlas al diagnostico. Esto ayuda a evitar combinaciones no recomendadas.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-search-alergias"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-panel-meds"]',
    popover: {
      title: 'MEDICAMENTOS',
      description:
        'En esta seccion agregas los medicamentos que formaran parte de la receta.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-panel-meds"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-search-medicamentos"]',
    popover: {
      title: 'BUSCAR MEDICAMENTO',
      description:
        'Escribe el nombre del medicamento. Al elegir uno de la lista, se abre la captura de posologia.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="rx-search-medicamentos"]', 8000);
      const cmp = (window as any)?.ng?.getComponent?.(
        document.querySelector('app-prescription-edit-demo')
      );
      cmp?.openMedicationAutocomplete?.();
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-med-results"]',
    popover: {
      title: 'RESULTADOS DE MEDICAMENTOS',
      description:
        'Aqui se muestran los medicamentos encontrados. Al seleccionar uno, debes completar su posologia.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR POSOLOGIA',
      onNextClick: () => {
        const cmp = (window as any)?.ng?.getComponent?.(
          document.querySelector('app-prescription-edit-demo')
        );
        cmp?.openPosologyModal?.();

        d.waitForElement('[data-tour="rx-line-modal"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="rx-med-results"]', 8000);
      const cmp = (window as any)?.ng?.getComponent?.(
        document.querySelector('app-prescription-edit-demo')
      );
      cmp?.openMedicationAutocomplete?.();
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-line-modal"]',
    popover: {
      title: 'POSOLOGIA DEL MEDICAMENTO',
      description:
        'En este modal se capturan cantidad, unidad, frecuencia, duracion y via de administracion del medicamento.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {
      d.waitForElement('[data-tour="rx-line-modal"]', 8000);
      const cmp = (window as any)?.ng?.getComponent?.(
        document.querySelector('app-prescription-edit-demo')
      );
      cmp?.openPosologyModal?.();
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-line-dose"]',
    popover: {
      title: 'CANTIDAD',
      description: 'Aqui indicas la cantidad que se administrara en cada toma.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-dose"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-unit"]',
    popover: {
      title: 'UNIDAD',
      description: 'Define la unidad de la dosis, por ejemplo comprimido, tableta o mililitros.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-unit"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-each"]',
    popover: {
      title: 'CADA',
      description: 'Aqui indicas cada cuanto se administra el medicamento, por ejemplo cada 8.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-each"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-freq-type"]',
    popover: {
      title: 'FRECUENCIA',
      description:
        'Define la unidad de tiempo de la administracion, por ejemplo horas o dias.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-freq-type"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-duration"]',
    popover: {
      title: 'DURACION',
      description:
        'Aqui se define por cuanto tiempo debera tomar el paciente el medicamento.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-duration"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-duration-type"]',
    popover: {
      title: 'PERIODO',
      description:
        'Aqui defines si la duracion se medira en dias o semanas.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-duration-type"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-route"]',
    popover: {
      title: 'VIA DE ADMINISTRACION',
      description:
        'Selecciona la via en que se administrara el medicamento, por ejemplo oral o sublingual.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-route"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-reason"]',
    popover: {
      title: 'MOTIVO DE USO',
      description:
        'Campo opcional para indicar el motivo clinico del medicamento, por ejemplo fiebre o dolor.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-reason"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-indication"]',
    popover: {
      title: 'INDICACIONES',
      description:
        'Aqui puedes agregar instrucciones adicionales de uso para el paciente. Este campo es opcional.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-indication"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-line-save"]',
    popover: {
      title: 'GUARDAR POSOLOGIA',
      description:
        'Cuando la posologia este completa, guarda para que el medicamento se agregue a la receta.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'GUARDAR',
      onNextClick: () => {
        const cmp = (window as any)?.ng?.getComponent?.(
          document.querySelector('app-prescription-edit-demo')
        );
        cmp?.savePosologyDemo?.();

        d.waitForElement('[data-tour="rx-action-docs"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-line-save"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-action-docs"]',
    popover: {
      title: 'VER DOCUMENTOS',
      description:
        'Este boton sirve para consultar documentos o informacion de apoyo del medicamento.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-action-docs"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-action-hide"]',
    popover: {
      title: 'OCULTAR MEDICAMENTO',
      description:
        'Este boton sirve para ocultar el medicamento en ciertos contextos de visualizacion o impresion timbrada.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-action-hide"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-edit-line"]',
    popover: {
      title: 'EDITAR POSOLOGIA',
      description:
        'Este boton permite volver a abrir la posologia del medicamento para corregir o ajustar la prescripcion.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-edit-line"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-action-delete-med"]',
    popover: {
      title: 'ELIMINAR MEDICAMENTO',
      description:
        'Este boton elimina el medicamento de la receta antes de guardarla definitivamente.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-action-delete-med"]', 8000),
  } as any);

  steps.push({
    element: '.rx-alerts-wrap',
    popover: {
      title: 'ALERTAS Y ANALISIS',
      description:
        'En esta seccion puedes consultar alertas criticas y ejecutar el analisis de la receta para revisar advertencias, interacciones o efectos adversos.',
      side: 'top',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('.rx-alerts-wrap', 8000),
  } as any);

  steps.push({
    element: '[data-tour="rx-save"]',
    popover: {
      title: 'GUARDAR RECETA',
      description:
        'Finalmente, cuando toda la informacion este correcta, aqui se guarda la receta.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'FINALIZAR',
      onNextClick: () => {
        d.finishAndNavigate?.('prescription/prescription-list', '.materialTableHeader');
      },
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="rx-save"]', 8000),
  } as any);

  return steps;
}

export function buildDoctorEditarRecetaSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  steps.push({
    element: '[data-tour="rx-create-prescription-entry"], [data-tour="top-widget-create-prescription"]',
    popover: {
      title: 'EDITAR RECETA',
      description:
        'A diferencia de crear receta, aqui el paciente ya esta cargado automaticamente.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => {
        d.navigateAndContinue('prescription/tutorial-edit', '[data-tour="rx-panel-paciente"]');
      },
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  // =========================================
  // RESUMEN CLINICO
  // =========================================
  steps.push({
    element: '[data-tour="rx-panel-paciente"] .rx-summary-card',
    popover: {
      title: 'RESUMEN CLINICO',
      description:
        'Aqui puedes revisar antecedentes y medicamentos activos antes de modificar la receta.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  // =========================================
  // DATOS CONSULTA
  // =========================================
  steps.push({
    element: '[data-tour="rx-panel-consulta"]',
    popover: {
      title: 'DATOS DE CONSULTA',
      description:
        'Puedes actualizar los datos clinicos del paciente antes de modificar los medicamentos.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-weight"]',
    popover: {
      title: 'PESO',
      description: 'Modifica el peso si es necesario.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-height"]',
    popover: {
      title: 'ALTURA',
      description: 'Modifica la altura si es necesario.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  // =========================================
  // DIAGNOSTICO
  // =========================================
  steps.push({
    element: '[data-tour="rx-panel-diagnostico"]',
    popover: {
      title: 'DIAGNOSTICO',
      description:
        'Puedes agregar o modificar patologias y alergias.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-cronico-checkbox"]',
    popover: {
      title: 'CRONICO',
      description:
        'Marca si la patologia es cronica.',
      side: 'top',
      align: 'center',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  // =========================================
  // MEDICAMENTOS (LO IMPORTANTE DEL EDIT)
  // =========================================
  steps.push({
    element: '[data-tour="rx-panel-meds"]',
    popover: {
      title: 'MEDICAMENTOS',
      description:
        'Aqui puedes modificar los medicamentos existentes o agregar nuevos.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-edit-line"]',
    popover: {
      title: 'EDITAR POSOLOGIA',
      description:
        'Este boton permite editar la posologia del medicamento.',
      side: 'left',
      align: 'center',
      nextBtnText: 'ABRIR',
      onNextClick: () => {
        const cmp = (window as any)?.ng?.getComponent?.(
          document.querySelector('app-prescription-edit-demo')
        );
        cmp?.openPosologyModal?.();

        d.waitForElement('[data-tour="rx-line-modal"]', 8000)
          .then(() => d.moveNextSafe());
      },
    },
  } as any);

  // =========================================
  // MODAL POSOLOGIA
  // =========================================
  steps.push({
    element: '[data-tour="rx-line-modal"]',
    popover: {
      title: 'POSOLOGIA',
      description:
        'Aqui puedes modificar dosis, frecuencia y duracion.',
      side: 'top',
      align: 'center',
      nextBtnText: 'SIGUIENTE',
    },
  } as any);

  steps.push({
    element: '[data-tour="rx-line-save"]',
    popover: {
      title: 'GUARDAR CAMBIOS',
      description:
        'Guarda los cambios del medicamento.',
      side: 'bottom',
      align: 'start',
      nextBtnText: 'GUARDAR',
      onNextClick: () => {
        const cmp = (window as any)?.ng?.getComponent?.(
          document.querySelector('app-prescription-edit-demo')
        );
        cmp?.savePosologyDemo?.();

        d.waitForElement('[data-tour="rx-save"]', 8000)
          .then(() => d.moveNextSafe());
      },
    },
  } as any);

  // =========================================
  // GUARDAR RECETA
  // =========================================
  steps.push({
    element: '[data-tour="rx-save"]',
    popover: {
      title: 'GUARDAR RECETA',
      description:
        'Guarda los cambios realizados en la receta.',
      side: 'top',
      align: 'center',
      doneBtnText: 'FINALIZAR',
      onNextClick: () => {
        d.finishAndNavigate?.('prescription/prescription-list', '.materialTableHeader');
      },
    },
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · SURTIR RECETA
// =========================================
export function buildStaffSurtirRecetaSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  steps.push({
    element: '[data-tour="sidebar-prescription-prescription-supply"]',
    popover: {
      title: 'SURTIR RECETA',
      description: 'Aqui surtimos una receta con su folio o con QR.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => d.navigateAndContinue('prescription/prescription-supply', '[data-tour="supply-folio"]'),
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  steps.push({
    element: '[data-tour="supply-folio"]',
    popover: {
      title: 'FOLIO',
      description: 'Ingresa el FOLIO de la receta.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="supply-folio"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="supply-scan"]',
    popover: {
      title: 'ESCANEAR QR',
      description: 'Opcional: puedes escanear el QR para cargar el folio automaticamente.',
      side: 'bottom',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => {},
  } as any);

  steps.push({
    element: '[data-tour="supply-search"]',
    popover: {
      title: 'BUSCAR',
      description: 'Presiona para cargar la receta y sus medicamentos.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'CONTINUAR',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="supply-search"]', 8000),
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · VER RECETAS + MEDICAMENTOS (PACIENTE)
// =========================================
export function buildPacienteVerRecetasYMedsSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  steps.push({
    element: '[data-tour="sidebar-dashboard"]',
    popover: {
      title: 'MIS RECETAS',
      description: 'Aqui puedes ver tus recetas y entrar al detalle para ver tus medicamentos.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => d.navigateAndContinue('dashboard', '[data-tour="dash-my-prescriptions"]'),
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  // Espera a que cargue el listado del dashboard
  steps.push({
    element: '[data-tour="dash-my-prescriptions"]',
    popover: {
      title: 'LISTADO DE RECETAS',
      description: 'Aqui se muestra tu listado de recetas.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="dash-my-prescriptions"]', 8000),
  } as any);

  // Buscar (tu input ya tiene data-tour="pat-rx-search")
  if (d.elementExists('[data-tour="pat-rx-search"]')) {
    steps.push({
      element: '[data-tour="pat-rx-search"]',
      popover: {
        title: 'BUSCAR',
        description: 'Escribe aqui para filtrar tus recetas (folio, medico, sucursal, etc.).',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.waitForElement('[data-tour="pat-rx-search"]', 8000),
    } as any);
  }

  // Recargar (boton refresh)
  if (d.elementExists('[data-tour="pat-rx-refresh"]')) {
    steps.push({
      element: '[data-tour="pat-rx-refresh"]',
      popover: {
        title: 'RECARGAR',
        description: 'Actualiza el listado sin recargar toda la pagina.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.waitForElement('[data-tour="pat-rx-refresh"]', 8000),
    } as any);
  }

  // Ver medicamentos / detalle (boton layers)
  if (d.elementExists('[data-tour="pat-rx-details"]')) {
    steps.push({
      element: '[data-tour="pat-rx-details"]',
      popover: {
        title: 'VER MEDICAMENTOS',
        description: 'Entra al detalle de la receta para ver tus medicamentos e indicaciones.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'ABRIR',
        onNextClick: () => {
          // Click al primer boton visible de detalles
          (document.querySelector('[data-tour="pat-rx-details"]') as HTMLElement | null)?.click();

          // Si en la pantalla de detalle tienes estos data-tour, el tour continua ahi.
          // Si no existen, igual avanzara con timeout sin romper.
          d.waitForElement('[data-tour="pat-rx-detail-tab-meds"]', 8000).then(() => d.moveNextSafe());
        },
      },
      onHighlightStarted: () => d.waitForElement('[data-tour="pat-rx-details"]', 8000),
    } as any);

    // Tab Medicamentos (si existe en el detalle)
    steps.push({
      element: '[data-tour="pat-rx-detail-tab-meds"]',
      popover: {
        title: 'MEDICAMENTOS',
        description: 'En esta pestaña veras los medicamentos indicados en tu receta.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.waitForElement('[data-tour="pat-rx-detail-tab-meds"]', 8000),
    } as any);

    // Lista de medicamentos (si existe en el detalle)
    steps.push({
      element: '[data-tour="pat-rx-detail-meds-list"]',
      popover: {
        title: 'DETALLE DE MEDICAMENTOS',
        description: 'Aqui ves dosis, frecuencia, via, periodo, indicaciones y si fue surtido o no.',
        side: 'top',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.waitForElement('[data-tour="pat-rx-detail-meds-list"]', 8000),
    } as any);

    // Descargar PDF (si existe en el detalle)
    if (d.elementExists('[data-tour="pat-rx-detail-download"]')) {
      steps.push({
        element: '[data-tour="pat-rx-detail-download"]',
        popover: {
          title: 'DESCARGAR RECETA',
          description: 'Si esta disponible, puedes descargar el PDF de tu receta.',
          side: 'left',
          align: 'center',
          prevBtnText: 'ANTERIOR',
          nextBtnText: 'SIGUIENTE',
        },
        onHighlightStarted: () => d.waitForElement('[data-tour="pat-rx-detail-download"]', 8000),
      } as any);
    }

    // Cierre (en el detalle)
    steps.push({
      element: '[data-tour="pat-rx-detail-meds-list"]',
      popover: {
        title: 'LISTO',
        description: 'Con esto ya puedes consultar tus recetas y ver tus medicamentos.',
        side: 'top',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        doneBtnText: 'TERMINAR',
      },
    } as any);

    return steps;
  }

  // Si por alguna razon no existe el boton de detalle, cerramos aqui sin romper
  steps.push({
    element: '[data-tour="dash-my-prescriptions"]',
    popover: {
      title: 'LISTO',
      description: 'Con esto ya puedes consultar tus recetas desde el dashboard.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      doneBtnText: 'TERMINAR',
    },
  } as any);

  return steps;
}

// =========================================
// SUB-TOUR · VER EVENTOS DE SALUD (PACIENTE)
// =========================================
export function buildPacienteEventosSaludSteps(d: GuidedTourStepsDeps): DriveStep[] {
  const steps: DriveStep[] = [];

  steps.push({
    element: '[data-tour="sidebar-patient-health-event-list"]',
    popover: {
      title: 'EVENTOS DE SALUD',
      description: 'Aqui puedes ver y registrar eventos de salud.',
      side: 'right',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
      onNextClick: () => d.navigateAndContinue('patient/health-event-list', '.materialTableHeader'),
    },
    onHighlightStarted: () => d.ensureSidebarExpanded(),
  } as any);

  steps.push({
    element: '[data-tour="evt-search"]',
    popover: {
      title: 'BUSCAR',
      description: 'Filtra eventos por texto.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="evt-search"]', 8000),
  } as any);

  steps.push({
    element: '[data-tour="evt-create"]',
    popover: {
      title: 'CREAR EVENTO',
      description: 'Registra un nuevo evento de salud.',
      side: 'left',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'ABRIR',
      onNextClick: () => {
        (document.querySelector('[data-tour="evt-create"]') as HTMLElement | null)?.click();
        d.waitForElement('[data-tour="evt-modal"]', 8000).then(() => d.moveNextSafe());
      },
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="evt-create"]', 8000),
  } as any);

  const modalFields: Array<{ sel: string; title: string; desc: string }> = [
    { sel: '[data-tour="evt-type"]', title: 'TIPO DE EVENTO', desc: 'Selecciona el tipo de evento.' },
    { sel: '[data-tour="evt-med-activo"]', title: 'MEDICAMENTO ACTIVO', desc: 'Si aplica, selecciona un medicamento activo.' },
    { sel: '[data-tour="evt-date"]', title: 'FECHA', desc: 'Selecciona la fecha del evento.' },
    { sel: '[data-tour="evt-desc-text"]', title: 'DESCRIPCION', desc: 'Describe el evento de salud.' },
  ];

  for (const mf of modalFields) {
    steps.push({
      element: mf.sel,
      popover: {
        title: mf.title,
        description: mf.desc,
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        nextBtnText: 'SIGUIENTE',
      },
      onHighlightStarted: () => d.waitForElement(mf.sel, 8000),
    } as any);
  }

  // ✅ GUARDAR: ahora si hace click y luego continua
  steps.push({
    element: '[data-tour="evt-save"]',
    popover: {
      title: 'GUARDAR',
      description: 'Guarda el evento de salud.',
      side: 'top',
      align: 'center',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'GUARDAR',
      onNextClick: async () => {

        // 1) Click en GUARDAR
        (document.querySelector('[data-tour="evt-save"]') as HTMLElement | null)?.click();

        // 2) Esperar un poquito a que Angular procese submit/close automatico si aplica
        await new Promise<void>((r) => setTimeout(r, 350));

        // 3) Si el modal sigue abierto, lo cerramos con el boton X (tu HTML lo tiene)
        if (document.querySelector('[data-tour="evt-modal"]')) {
          (document.querySelector('[data-tour="evt-modal"] .modal-close-button') as HTMLElement | null)?.click();
        }

        // 4) Esperar a que desaparezca el modal (ya con helper)
        if (d.waitForNotExists) {
          await d.waitForNotExists('[data-tour="evt-modal"]', 8000);
        } else {
          // fallback por si no llego deps (no deberia pasar)
          await new Promise<void>((r) => setTimeout(r, 700));
        }

        // 5) Esperar el boton de DETALLES (ya visible, sin overlay)
        await d.waitForElement('[data-tour="evt-desc"]', 8000);

        // 6) Avanzar al step de DETALLES
        d.moveNextSafe();
      },
    },
    onHighlightStarted: () => d.waitForElement('[data-tour="evt-save"]', 8000),
  } as any);


  // ✅ Paso de "despedida" / confirmacion post-guardado
  steps.push({
    element: '.materialTableHeader',
    popover: {
      title: 'LISTO',
      description: 'Tu evento se guardo. Ya debe aparecer en el listado y podras consultarlo cuando quieras.',
      side: 'bottom',
      align: 'start',
      prevBtnText: 'ANTERIOR',
      nextBtnText: 'SIGUIENTE',
    },
    onHighlightStarted: () => d.waitForElement('.materialTableHeader', 8000),
  } as any);

  // Accion de ver descripcion/detalle (si existe)
  if (d.elementExists('[data-tour="evt-desc"]')) {
    steps.push({
      element: '[data-tour="evt-desc"]',
      popover: {
        title: 'VER DESCRIPCION',
        description: 'Puedes abrir el detalle/descripcion del evento.',
        side: 'left',
        align: 'center',
        prevBtnText: 'ANTERIOR',
        doneBtnText: 'TERMINAR',
      },
      onHighlightStarted: () => d.waitForElement('[data-tour="evt-desc"]', 8000),
    } as any);
  } else {
    steps.push({
      element: '.materialTableHeader',
      popover: {
        title: 'TERMINADO',
        description: 'Con esto ya sabes como crear y consultar tus eventos de salud.',
        side: 'bottom',
        align: 'start',
        prevBtnText: 'ANTERIOR',
        doneBtnText: 'TERMINAR',
      },
    } as any);
  }

  return steps;
}


